import { useState } from "react";
import { Upload, ChevronDown } from "lucide-react";
import { parseThinkorSwimCSV, validateTOSFormat } from "../../utils/tosParser";
import {
  parseNinjaTraderCSV,
  validateNinjaFormat,
} from "../../utils/ninjaParser";
import {
  parseWebullCSV,
  validateWebullFormat,
} from "../../utils/webullParserII";
import {
  parseTopStepXCSV,
  validateTopStepXFormat,
} from "../../utils/topstepxParser";
import { saveTradesToFirestore } from "../../utils/firebaseTradeUtils";
import { useAuth } from "../../context/AuthContext";
import Toast from "../Toast/Toast";

const CsvImport = () => {
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedBroker, setSelectedBroker] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvContent, setCsvContent] = useState(null);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const getCurrentUser = () => {
    return {
      email: user?.email || "",
      firstName: user?.displayName?.split(" ")[0] || "",
      lastName: user?.displayName?.split(" ")[1] || "",
    };
  };

  const formatAccountName = (username) => {
    return `${username.substring(0, 6)}...Mastery`;
  };

  const currentUser = getCurrentUser();
  const accounts = [
    {
      id: "1",
      name: formatAccountName(currentUser.email.split("@")[0]),
    },
  ];

  const brokers = [
    {
      id: "tos",
      name: "ThinkorSwim",
      logo: "/api/placeholder/40/40",
      instructions: [
        "Log in to ThinkOrSwim",
        "Navigate to Monitor > Account Statement",
        "Select desired date range",
        'Click "Export" and choose CSV format',
        "Save the file to your computer",
      ],
    },
    {
      id: "ninja",
      name: "NinjaTrader",
      logo: "/api/placeholder/40/40",
      instructions: [
        "Log in to NinjaTrader",
        "Navigate to Control Center > Executions",
        "Select desired date range",
        "Right-click and select 'Export to Excel'",
        "Save the file as CSV",
      ],
    },
    {
      id: "webull",
      name: "Webull",
      logo: "/api/placeholder/40/40",
      instructions: [
        "Log in to Webull",
        "Navigate to Orders",
        "Select 'Download Orders Records'",
        "Save the file as CSV",
      ],
    },
    {
      id: "topstepx",
      name: "TopStepX",
      logo: "/api/placeholder/40/40",
      instructions: [
        "Log in to TopStepX",
        "Navigate to Order History",
        "Click 'Export' or 'Download CSV'",
        "Save the file to your computer",
      ],
    },
  ];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    try {
      const text = await file.text();
      setCsvContent(text);
      setSelectedFile(file);
      setError(null);
    } catch (err) {
      setError("Error reading file. Please try again.");
      setToast({
        type: "error",
        message: "Trades Could Not Be Imported",
        subText: "Error reading file. Please try again.",
      });
    }
  };

  const handleSubmit = async () => {
    if (!csvContent || !user) {
      console.log("Missing content or user:", {
        csvContent: !!csvContent,
        user: !!user,
      });
      setToast({
        type: "error",
        message: "Trades Could Not Be Imported",
        subText: !csvContent
          ? "No file selected"
          : "Please sign in to import trades",
      });
      return;
    }

    try {
      let validation;
      let parsedTrades;
      let accountBalance = 0; // Initialize to 0

      if (selectedBroker === "tos") {
        console.log("Processing ThinkorSwim CSV...");
        console.log("CSV Content Preview:", csvContent.substring(0, 500));

        validation = validateTOSFormat(csvContent);
        console.log("TOS Validation result:", validation);

        if (validation.isValid) {
          const result = parseThinkorSwimCSV(csvContent);
          console.log("Parsed TOS result:", result);
          parsedTrades = result.trades;
          accountBalance = result.accountBalance || 0;
        }
      } else if (selectedBroker === "ninja") {
        console.log("Processing NinjaTrader CSV...");
        validation = validateNinjaFormat(csvContent);
        console.log("NinjaTrader Validation result:", validation);

        if (validation.isValid) {
          const result = parseNinjaTraderCSV(csvContent);
          console.log("Parsed NinjaTrader result:", result);
          parsedTrades = result.trades;
          accountBalance = result.accountBalance || 0;
          console.log("Parsed NinjaTrader trades:", parsedTrades);
        }
      } else if (selectedBroker === "webull") {
        console.log("Processing Webull CSV...");
        validation = validateWebullFormat(csvContent);
        console.log("Webull Validation result:", validation);

        if (validation.isValid) {
          const result = parseWebullCSV(csvContent);
          console.log("Parsed Webull result:", result);
          parsedTrades = result.trades;
          accountBalance = result.accountBalance || 0;
          console.log("Parsed Webull trades:", parsedTrades);
        }
      } else if (selectedBroker === "topstepx") {
        console.log("Processing TopStepX CSV...");
        validation = validateTopStepXFormat(csvContent);
        console.log("TopStepX Validation result:", validation);

        if (validation.isValid) {
          const result = parseTopStepXCSV(csvContent);
          console.log("Parsed TopStepX result:", result);
          parsedTrades = result.trades;
          accountBalance = result.accountBalance || 0;
          console.log("Parsed TopStepX trades:", parsedTrades);
        }
      }

      if (!validation.isValid) {
        setToast({
          type: "error",
          message: "Trades Could Not Be Imported",
          subText: validation.message,
        });
        return;
      }

      if (!parsedTrades || parsedTrades.length === 0) {
        console.log("No trades were parsed from the CSV");
        setToast({
          type: "error",
          message: "No Trades Found",
          subText: "The file contained no valid trades to import",
        });
        return;
      }

      console.log("Attempting to save trades to Firestore...");
      console.log("User ID:", user.uid);
      console.log("Number of trades:", parsedTrades.length);

      // Save to Firestore
      const saveResult = await saveTradesToFirestore(
        user.uid,
        parsedTrades,
        selectedBroker,
        accountBalance
      );
      console.log("Trades successfully saved to Firestore");

      // Force refresh dashboards by triggering both standard events
      window.dispatchEvent(new Event("tradesUpdated"));
      window.dispatchEvent(new Event("storage"));

      setToast({
        type: "success",
        message: "Trades Imported Successfully",
        subText: `${parsedTrades.length} trades were imported`,
      });

      // Clear form
      setSelectedFile(null);
      setCsvContent(null);
    } catch (err) {
      console.error("Import error:", err);
      setToast({
        type: "error",
        message: "Trades Could Not Be Imported",
        subText: "Error saving trades. Please try again.",
      });
    }
  };

  const saveBrokerSelection = (brokerId) => {
    setSelectedBroker(brokerId);
    localStorage.setItem("lastUsedBroker", brokerId);
  };

  const renderBrokerInstructions = () => {
    const broker = brokers.find((b) => b.id === selectedBroker);
    if (!broker) return null;

    return (
      <div className="broker-instructions">
        <div className="instructions-col">
          <h3>How to Export from {broker.name}</h3>
          <div className="video-container">
            <img
              src="/api/placeholder/560/315"
              alt="How to export trades"
              className="instruction-video"
            />
          </div>
          <div className="instruction-steps">
            <h4>Steps to export your trades:</h4>
            <ol>
              {broker.instructions.map((instruction, index) => (
                <li key={index}>{instruction}</li>
              ))}
            </ol>
          </div>
        </div>

        <div className="upload-col">
          <div
            className={`drop-zone ${dragActive ? "active" : ""}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="drop-zone-content">
              <Upload size={48} />
              <h3>Upload your {broker.name} CSV</h3>
              <p>or</p>
              <label className="file-input-label">
                Choose File
                <input
                  type="file"
                  className="file-input"
                  onChange={handleFileInput}
                  accept=".csv"
                />
              </label>
              <p className="file-types">Supported format: CSV</p>
            </div>
          </div>

          {selectedFile && (
            <div className="selected-file">
              <h4>Selected File:</h4>
              <p>{selectedFile.name}</p>
              <button
                className="submit-button"
                onClick={handleSubmit}
                disabled={!selectedFile}
              >
                Import Trades
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="csv-import-container">
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          subText={toast.subText}
          onClose={() => setToast(null)}
        />
      )}

      <h2>Upload your CSV</h2>
      <p className="csv-description">
        Please select an account you'll like to import your CSV to
      </p>

      <div className="import-selectors">
        <div className="select-group">
          <label htmlFor="account">Select Account</label>
          <div className="custom-select">
            <select
              id="account"
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
            >
              <option value="">Choose an account...</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
            <ChevronDown size={20} />
          </div>
        </div>

        <div className="select-group">
          <label htmlFor="broker">Select Broker</label>
          <div className="custom-select broker-select">
            <select
              id="broker"
              value={selectedBroker}
              onChange={(e) => saveBrokerSelection(e.target.value)}
            >
              <option value="">Choose a broker...</option>
              {brokers.map((broker) => (
                <option
                  key={broker.id}
                  value={broker.id}
                  data-image={broker.logo}
                >
                  {broker.name}
                </option>
              ))}
            </select>
            <div className="select-icon">
              {selectedBroker ? (
                <img
                  src={brokers.find((b) => b.id === selectedBroker)?.logo}
                  alt="Broker logo"
                  className="broker-logo"
                />
              ) : null}
              <ChevronDown size={20} />
            </div>
          </div>
        </div>
      </div>

      {selectedAccount && selectedBroker && renderBrokerInstructions()}
    </div>
  );
};

export default CsvImport;
